import { Location } from '@cobliteam/common-types';
import { DatadogTrack } from '@cobliteam/datadog-rum';
import { Button, Typography } from '@cobliteam/ui/components';
import Icon from '@cobliteam/ui/icons';
import { useEffect } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { Link } from 'react-router-dom';

type ErrorPageProps = {
  path?: string;
} & FallbackProps;

const ErrorPage = ({ error, path, resetErrorBoundary }: ErrorPageProps) => {
  useEffect(() => {
    const enhancedError = new Error(`ErrorBoundary error on path "${path ?? 'unknown'}": ${error?.message ?? 'No message'}`);
    enhancedError.stack = error?.stack;

    DatadogTrack.error(enhancedError, {
      component: 'ErrorPage',
      path,
      route: path,
      errorMessage: error?.message,
      errorStack: error?.stack,
      errorName: error?.name,
      timestamp: new Date().toISOString(),
    });

    return () => {
      resetErrorBoundary();
    };
  }, [path, error, resetErrorBoundary]);

  return (
    <div className='flex h-full flex-col items-center justify-center gap-4 text-center'>
      <Icon name='wifi-off' size='4xl' className='box-content overflow-visible rounded-full bg-primary-2 p-10' />
      <Typography variant='heading-2xl' className='font-bold' as='h1'>
        Ops! Algo deu errado
      </Typography>
      <Typography variant='input-placeholder'>
        Encontramos um problema inesperado. Nossa equipe foi notificada e está trabalhando para resolver isso o mais rápido possível.
      </Typography>
      {process.env.NODE_ENV === 'development' && (
        <div className='mt-2 rounded-md bg-gray-2 p-3 text-left text-sm'>
          <Typography variant='text-sm-medium' className='mb-1'>
            Detalhes técnicos:
          </Typography>
          <Typography variant='text-sm' className='font-mono text-gray-11'>
            {error.message}
          </Typography>
          {path && (
            <Typography variant='text-sm' className='mt-1 font-mono text-gray-11'>
              Rota: {path}
            </Typography>
          )}
        </div>
      )}
      <div className='flex gap-2'>
        <Link to={Location.HOME}>
          <Button variant='outlined' startIcon={<Icon name='house' />}>
            Ir para o início
          </Button>
        </Link>
        <Button variant='ghost' startIcon={<Icon name='rotate-cw' />} onClick={() => window.location.reload()}>
          Tentar novamente
        </Button>
      </div>
    </div>
  );
};

export default ErrorPage;
